import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Drawer,
  Field,
  Input,
  Select,
  useToast,
} from "../../../design-system";
import EmployeeMultiPicker from "../../../Components/Kronaly/Empleados/EmployeeMultiPicker";
import { bulkCreateDocumentRequests } from "../../../helpers/documents_helper";
import { TEXTAREA_STYLE } from "../constants";

export function RequestDocumentDrawer({
  open,
  onClose,
  employees = [],
  categories = [],
  defaultEmployeeProfileId = "",
  onCreated,
}) {
  const toast = useToast();
  const initialForm = () => ({
    employee_profile_ids: defaultEmployeeProfileId ? [defaultEmployeeProfileId] : [],
    category_root_id: categories?.[0]?.id || "",
    category_sub_id: "",
    title: "",
    message: "",
  });
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm((prev) => ({
        ...prev,
        employee_profile_ids: defaultEmployeeProfileId
          ? Array.from(new Set([defaultEmployeeProfileId, ...prev.employee_profile_ids]))
          : prev.employee_profile_ids,
        category_root_id: prev.category_root_id || categories?.[0]?.id || "",
      }));
    } else {
      setForm(initialForm());
      setError(null);
    }
  }, [open, defaultEmployeeProfileId, categories]);

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const rootCategory = (categories || []).find((c) => c.id === form.category_root_id) || null;
  const subOptions = rootCategory?.children || [];
  const count = form.employee_profile_ids.length;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (count === 0) {
      setError("Selecciona al menos un empleado.");
      return;
    }
    if (!form.title.trim()) {
      setError("El título es obligatorio.");
      return;
    }
    const categoryId = form.category_sub_id || form.category_root_id;
    if (!categoryId) {
      setError("Selecciona una categoría.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await bulkCreateDocumentRequests({
        employee_profile_ids: form.employee_profile_ids,
        category_id: categoryId,
        title: form.title.trim(),
        message: form.message.trim() || undefined,
      });
      const created = res?.data?.created_count ?? count;
      toast.success(
        created === 1
          ? "Solicitud enviada"
          : `${created} solicitudes enviadas`,
      );
      onCreated?.();
    } catch (e2) {
      setError(typeof e2 === "string" ? e2 : e2?.message || "No se pudieron enviar las solicitudes.");
    } finally {
      setSubmitting(false);
    }
  };

  const formId = "request-document-form";
  const submitLabel = submitting
    ? "Enviando…"
    : count <= 1
      ? "Enviar solicitud"
      : `Enviar ${count} solicitudes`;

  return (
    <Drawer
      open={open}
      onClose={submitting ? undefined : onClose}
      title={count <= 1 ? "Solicitar documento" : `Solicitar documento a ${count} empleados`}
      size="md"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={submitting}>Cancelar</Button>
          <div style={{ flex: 1 }} />
          <Button variant="primary" type="submit" form={formId} icon="Send" disabled={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {error && <Alert tone="danger">{error}</Alert>}

        <Field label="Empleados" required help="Cada empleado recibirá su propia solicitud con el mismo enunciado.">
          <EmployeeMultiPicker
            employees={employees}
            value={form.employee_profile_ids}
            onChange={(ids) => update({ employee_profile_ids: ids })}
          />
        </Field>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Categoría" required>
            <Select
              value={form.category_root_id}
              onChange={(e) => update({ category_root_id: e.target.value, category_sub_id: "" })}
            >
              <option value="">— Selecciona —</option>
              {(categories || []).map((root) => (
                <option key={root.id} value={root.id}>{root.name}</option>
              ))}
            </Select>
          </Field>
          <Field
            label="Subcategoría"
            help={subOptions.length === 0 ? "Sin subcategorías." : undefined}
          >
            <Select
              value={form.category_sub_id}
              onChange={(e) => update({ category_sub_id: e.target.value })}
              disabled={subOptions.length === 0}
            >
              <option value="">— Ninguna —</option>
              {subOptions.map((sub) => (
                <option key={sub.id} value={sub.id}>{sub.name}</option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Título" required>
          <Input
            value={form.title}
            onChange={(e) => update({ title: e.target.value })}
            placeholder="Ej. Sube tu DNI por ambas caras"
          />
        </Field>

        <Field label="Mensaje" help="Explica al empleado qué documento necesitas.">
          <textarea
            value={form.message}
            onChange={(e) => update({ message: e.target.value })}
            rows={5}
            placeholder="Detalles, formato, plazos…"
            style={TEXTAREA_STYLE}
          />
        </Field>
      </form>
    </Drawer>
  );
}
